// AuthProvider - Updated to match mobile app structure
import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useRef,
} from "react";
import type { ReactNode } from "react";
import { API_URL, getAuthHeaders } from "@/shared/config";
import { registerUnauthorizedCleanup } from "@/shared/lib/apiInterceptor";
import {
  clearStoredTokens,
  registerTokenRefreshListener,
  setStoredTokens,
  TOKEN_KEY,
} from "@/shared/lib/authTokens";
import type { ApiTheme } from "@/types/theme";

// Types matching mobile app structure
interface Building {
  pk: number;
  name: string;
  pkUser: string;
  refund_deadline_hours?: number;
  /** GPS latitude. Optional; use for region/map. */
  latitude: number | null;
  /** GPS longitude. Optional. */
  longitude: number | null;
}

/** Parse number or numeric string to number; otherwise null. Ensures lat/lon work when API sends strings. */
function toNum(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(",", "."));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function buildingPkFromRaw(b: Record<string, unknown>): number {
  const raw = b.pk ?? b.id;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/** Normalize building list from API or storage. Backend may omit fields or send lat/lon as strings. */
function normalizeBuildings(raw: unknown): Building[] {
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list
    .map((b: Record<string, unknown>) => {
      const pk = buildingPkFromRaw(b);
      return {
        pk,
        name: String(b.name ?? ""),
        pkUser: String(b.pkUser ?? b.pk ?? b.id ?? ""),
        refund_deadline_hours:
          typeof b.refund_deadline_hours === "number"
            ? b.refund_deadline_hours
            : 48,
        latitude: toNum(b.latitude),
        longitude: toNum(b.longitude),
      };
    })
    .filter((row) => Number.isFinite(row.pk));
}

interface User {
  banner: boolean;
  email: string;
  firstName: string;
  lastName: string;
  nameEntreprise: string;
  pk: number;
  id: number; // Add id property for compatibility
  role: string;
  theme?: ApiTheme | null;
  vip: {
    email: string;
    lan: boolean;
    name_resa: string;
  };
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  building: Building[] | null;
  currentBuilding: Building | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: (tkn?: string) => Promise<void>;
  /** Bâtiment courant (doit être dans ``building``). */
  selectBuilding: (b: Building) => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  user: null,
  building: null,
  currentBuilding: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  refreshUser: async () => {},
  selectBuilding: () => {},
});

const USER_INFO_ETAG_KEY = "hagd_user_info_etag";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [building, setBuilding] = useState<Building[] | null>(null);
  const [currentBuilding, setCurrentBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const userInfoEtagRef = useRef<string | null>(null);

  // Load token and user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem("hagd_user");
    const storedBuilding = localStorage.getItem("hagd_building");
    const storedCurrentBuilding = localStorage.getItem("hagd_currentBuilding");

    if (storedToken) {
      setToken(storedToken);
      const storedEtag = localStorage.getItem(USER_INFO_ETAG_KEY);
      if (storedEtag) userInfoEtagRef.current = storedEtag;
    }

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse stored user:", e);
      }
    }

    if (storedBuilding) {
      try {
        const parsed = JSON.parse(storedBuilding);
        const buildings = normalizeBuildings(parsed);
        setBuilding(buildings);
        // Set current building if available
        if (storedCurrentBuilding) {
          try {
            const current = JSON.parse(storedCurrentBuilding);
            const withInsee = normalizeBuildings([current])[0];
            setCurrentBuilding(withInsee);
          } catch (e) {
            console.error("Failed to parse stored current building:", e);
            if (buildings.length > 0) setCurrentBuilding(buildings[0]);
          }
        } else if (buildings.length > 0) {
          setCurrentBuilding(buildings[0]);
        }
      } catch (e) {
        console.error("Failed to parse stored building:", e);
      }
    }

    setLoading(false);
  }, []);

  // Refresh user from API when token exists (sync theme and other fields)
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      refreshUser(storedToken);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/users/token-auth/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === "account_deactivated") {
          const deactivatedError = new Error("account_deactivated");
          (deactivatedError as Error & { code?: string }).code =
            "account_deactivated";
          throw deactivatedError;
        }
        throw new Error(
          errorData.message || errorData.detail || "Login failed"
        );
      }

      const data = await response.json();
      const authToken = data.token || data.key;

      if (!authToken) {
        throw new Error("No token received from server");
      }

      setToken(authToken);
      if (data.refresh) {
        setStoredTokens(authToken, data.refresh);
      } else {
        localStorage.setItem(TOKEN_KEY, authToken);
      }

      await refreshUser(authToken);
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setBuilding(null);
    setCurrentBuilding(null);
    userInfoEtagRef.current = null;
    clearStoredTokens();
    localStorage.removeItem("hagd_user");
    localStorage.removeItem("hagd_building");
    localStorage.removeItem("hagd_currentBuilding");
    localStorage.removeItem(USER_INFO_ETAG_KEY);
  }, []);

  useEffect(() => {
    const unregister = registerTokenRefreshListener((accessToken) => {
      setToken(accessToken);
    });
    return unregister;
  }, []);

  useEffect(() => {
    const unregister = registerUnauthorizedCleanup(() => {
      logout();
    });

    return unregister;
  }, [logout]);

  const selectBuilding = useCallback(
    (next: Building) => {
      if (!Number.isFinite(next.pk)) return;
      if (!building?.some((b) => Number(b.pk) === Number(next.pk))) return;
      setCurrentBuilding(next);
      localStorage.setItem("hagd_currentBuilding", JSON.stringify(next));
    },
    [building],
  );

  const refreshUser = async (tkn?: string) => {
    const authToken = tkn || token;
    if (!authToken) return;

    const headers = getAuthHeaders(authToken);
    const userInfoEtag =
      userInfoEtagRef.current ?? localStorage.getItem(USER_INFO_ETAG_KEY);
    const userHeaders: Record<string, string> = { ...headers };
    if (userInfoEtag) {
      userHeaders["If-None-Match"] = userInfoEtag;
    }

    try {
      const [buildingRes, userRes] = await Promise.all([
        fetch(`${API_URL}/users/deleteGetPatchUser/`, { headers }),
        fetch(`${API_URL}/users/getUserInfo/`, { headers: userHeaders }),
      ]);

      // Check for 401 errors - if token is invalid, don't proceed
      if (buildingRes.status === 401 || userRes.status === 401) {
        console.error("Token invalid during refreshUser - status 401");
        // Don't throw here, just log - let the interceptor handle it if needed
        // But we should clear invalid token
        if (buildingRes.status === 401 && userRes.status === 401) {
          // Both failed, token is definitely invalid
          logout();
          return;
        }
      }

      // Handle building data (GET /users/deleteGetPatchUser/ returns [{ pk, name, pkUser, code_insee? }])
      if (buildingRes.ok) {
        try {
          const buildingData = await buildingRes.json();
          const buildings = normalizeBuildings(buildingData);
          setBuilding(buildings);
          localStorage.setItem("hagd_building", JSON.stringify(buildings));

          if (buildings.length > 0) {
            setCurrentBuilding((prev) => {
              const prevPk =
                prev != null && Number.isFinite(Number(prev.pk))
                  ? Number(prev.pk)
                  : NaN;
              const next =
                Number.isFinite(prevPk) &&
                buildings.some((b) => Number(b.pk) === prevPk)
                  ? buildings.find((b) => Number(b.pk) === prevPk) ?? buildings[0]
                  : buildings[0];
              localStorage.setItem("hagd_currentBuilding", JSON.stringify(next));
              return next;
            });
          } else {
            setCurrentBuilding(null);
            localStorage.removeItem("hagd_currentBuilding");
          }
        } catch (parseError) {
          console.error("Error parsing building data:", parseError);
        }
      } else if (buildingRes.status !== 401) {
        // Log non-401 errors
        console.warn("Failed to fetch building data:", buildingRes.status, buildingRes.statusText);
      }

      // Handle user data (200 = full body, 304 = not modified, use cached)
      if (userRes.status === 304) {
        // Not modified – keep existing user state
      } else if (userRes.ok) {
        try {
          const etag = userRes.headers.get("ETag");
          if (etag) {
            userInfoEtagRef.current = etag;
            localStorage.setItem(USER_INFO_ETAG_KEY, etag);
          }
          const userData = await userRes.json();
          if (userData && userData.pk && !userData.id) {
            userData.id = userData.pk;
          }
          setUser(userData);
          localStorage.setItem("hagd_user", JSON.stringify(userData));
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
        }
      } else if (userRes.status !== 401) {
        console.warn("Failed to fetch user data:", userRes.status, userRes.statusText);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
      // Don't throw - we don't want to break the login flow
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        building,
        currentBuilding,
        loading,
        login,
        logout,
        refreshUser,
        selectBuilding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
